"use client";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import dynamic from 'next/dynamic';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
const officialLogo = '/assets/hirebee-logo-official.svg';
import { useAuth } from '@/hooks/useAuth';
import { trackPageView } from '@/utils/signupAnalytics';
import { trackCtaClick } from '@/utils/ctaTracking';
import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import {
  Users, Calendar, CreditCard, Target, BarChart3,
  Workflow, Clock, Shield, ChevronRight, Menu, X, Building2, Play, MessageCircle, Mail,
  GraduationCap, Brain, Award, Smartphone, HeartHandshake, BookOpen, Calculator, Zap,
  Linkedin, Facebook, Instagram, Youtube, ArrowUp, MapPin, Phone, Loader2, ShoppingBag,
  Wrench, FileText, Building, Rocket, Briefcase, Globe, Hospital, Cpu, Factory, Heart, Laptop, Layers
} from 'lucide-react';
import { productCategories } from '@/data/productCategoriesData';
import { UrgencyBanner } from '@/components/landing/UrgencyBanner';
import { useUrgencyBannerState } from '@/hooks/useUrgencyBannerState';
import { usePageViewTracking } from '@/hooks/usePageViewTracking';
import { useProductLinkTracking } from '@/hooks/useProductLinkTracking';
import { useCtaBannerTracking } from '@/hooks/useCtaBannerTracking';
import { useSiteLinkTracking } from '@/hooks/useSiteLinkTracking';
// PERF: the product picker only appears after a click, but its static import pulled
// Radix Dialog + a batch of icons into the header chunk that hydrates on EVERY
// marketing page. next/dynamic moves it to its own chunk fetched on first open.
const MobileMenu = dynamic(() => import('@/components/marketing/MobileMenu'), { ssr: false });

const ProductSelectionModal = dynamic(
  () => import('@/components/marketing/ProductSelectionModal').then((m) => m.ProductSelectionModal),
  { ssr: false },
);

// Client island: the interactive header (scroll state, mobile Sheet menu,
// desktop mega-menu). Split out of MarketingLayout so the static shell + footer
// stay server-rendered (no hydration).
export function SiteHeader() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Latches on the first open so the menu chunk is fetched once and then stays mounted.
  const [mobileMenuMounted, setMobileMenuMounted] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const { isVisible: isBannerVisible, dismiss: dismissBanner } = useUrgencyBannerState();
  const headerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    // Read size off the observer entry itself (not el.offsetHeight) — other
    // ResizeObservers on the page fire in the same notification batch, and
    // re-querying the DOM here would risk a forced synchronous reflow if one
    // of those runs first and invalidates layout.
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
      document.documentElement.style.setProperty('--site-header-height', `${height}px`);
    });
    observer.observe(el, { box: 'border-box' });
    return () => observer.disconnect();
  }, []);

  const onNavValueChange = useCallback((value: string) => {
    document.body.style.overflow = value ? 'hidden' : '';
  }, []);
  usePageViewTracking();
  // Records the click AND attaches the visit identity to any app/hrms/assessments
  // link clicked anywhere on the page -- notably the 17 untracked pricing buttons.
  useProductLinkTracking();
  // Clicks on the CTA banners (FinalCTASection) and on buttons inside blog articles.
  useCtaBannerTracking();
  // Every other link to demo / pricing / contact / sign-up / the tools, and mail/phone
  // links -- the fallback for links with no tracker of their own (footer included).
  useSiteLinkTracking();

  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    trackPageView('marketing_page_view', {
      path: window.location.pathname,
      referrer: document.referrer || 'direct',
      utm_source: new URLSearchParams(window.location.search).get('utm_source'),
      utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
      utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign'),
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const MINT_HERO_ROUTES = [
    '/', '/demo', '/our-story', '/contacts', '/press', '/sitemap',
    '/privacy-policy', '/terms-of-use', '/cookie-policy', '/refund-policy',
    '/dpa', '/security', '/compliance', '/changelog',
    '/blog', '/resources', '/team', '/customer-stories', '/hr-tool-and-template',
    '/features', '/solutions', '/tools', '/hr-terms', '/help', '/status', '/roadmap', '/hirebee-vs-competitors',
    '/community', '/careers', '/bookmarks', '/glossary', '/compare',
    '/landing', '/events/register'
  ];
  const MINT_HERO_PREFIXES = [
    '/solutions/', '/features/', '/hrms/', '/assessment/', '/recruitment/',
    '/hirebee-vs/', '/business-type-', '/blog/', '/hr-terms/', '/customer-stories/'
  ];
  const hasMintHero = (MINT_HERO_ROUTES.includes(location.pathname) || MINT_HERO_PREFIXES.some(prefix => location.pathname.startsWith(prefix)))
    && !location.pathname.startsWith('/blog/author/');

  return (
    <>
      {/* Fixed header: banner + nav stacked — height measured via ResizeObserver */}
      <div ref={headerRef} data-site-header className="fixed top-0 left-0 right-0 z-[110]">
        <UrgencyBanner isVisible={isBannerVisible} onDismiss={dismissBanner} />
        <nav id="site-header-nav" className={`pt-[env(safe-area-inset-top)] will-change-transform transition-[background-color,border-color,box-shadow] duration-300 ${
        isScrolled || !hasMintHero
          ? 'bg-white border-b border-border shadow-sm'
          : 'bg-[#E9FBFA] border-b border-transparent'
      }`}>
        <div className="container mx-auto">
          <div className={`flex items-center justify-between transition-all duration-300 ${
            'h-16'
          }`}>
            {/* Mobile Hamburger - Left */}
            <div className="md:hidden flex items-center">
              <Button
                variant="ghost"
                size="sm"
                className="p-2 hover:bg-muted/50"
                onClick={() => {
                  setMobileMenuMounted(true);
                  setMobileMenuOpen(true);
                }}
              >
                <Menu className="h-6 w-6" />
                <span className="sr-only">Open menu</span>
              </Button>
            </div>

            {/* Mobile/Tablet Logo */}
            <Link to="/" className="flex md:hidden flex-1 items-center justify-center min-w-0 transition-transform duration-200 hover:scale-105">
              <img
                src={officialLogo}
                alt="Hirebee Logo" width={148} height={56}
                className="transition-all duration-300 w-auto object-contain h-9 max-w-full"
              />
            </Link>

            {/* Desktop Logo */}
            <Link to="/" className="hidden md:flex items-center transition-transform duration-200 hover:scale-105">
              <img
                src={officialLogo}
                alt="Hirebee Logo" width={148} height={56}
                className="transition-all duration-300 w-auto object-contain h-9"
              />
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center min-w-0">
              <NavigationMenu onValueChange={onNavValueChange}>
                <NavigationMenuList className="space-x-0 lg:space-x-2">
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="text-sm font-medium group">
                      Features
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-[900px] p-0 bg-gradient-to-br from-background via-background to-muted/20">
                        {/* Header */}
                        <div className="p-5 border-b bg-gradient-to-r from-blue-500/5 via-emerald-500/5 to-purple-500/5">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-bold text-foreground">Complete Talent & Workforce Platform</h3>
                              <p className="text-xs text-muted-foreground">From hire to retire — choose what you need</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button asChild size="sm" variant="default" className="text-xs shadow-sm">
                                <Link to="/features">View All Features →</Link>
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* 4-Column Grid */}
                        <div className="grid grid-cols-4 gap-4 p-6">
                          {/* 1. Recruitment & Hiring */}
                          <NavigationMenuLink asChild>
                            <Link to="/features?tab=recruitment" className="group block p-4 rounded-xl border border-border/50 bg-card/50 hover:bg-muted/50 hover:border-blue-500/30 hover:shadow-lg transition-all duration-200">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="p-2 rounded-lg bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                                  <Users className="h-4 w-4 text-blue-500" />
                                </div>
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">Core</span>
                              </div>
                              <h4 className="font-semibold text-sm text-foreground mb-2">Recruitment & Hiring</h4>
                              <div className="px-2 py-1 rounded-md bg-blue-500/5 text-xs font-medium text-blue-600 mb-3">⚡ 60% Faster Hiring</div>
                              <ul className="space-y-2 mb-4">
                                <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Brain className="h-3 w-3 text-blue-500 shrink-0" /> AI Resume Screening
                                </li>
                                <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Calendar className="h-3 w-3 text-blue-500 shrink-0" /> Interview Scheduling
                                </li>
                                <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Target className="h-3 w-3 text-blue-500 shrink-0" /> Multi-Channel Job Posts
                                </li>
                              </ul>
                              <div className="text-xs font-medium text-primary flex items-center gap-1 group-hover:gap-2 transition-all">
                                Explore Recruitment <ChevronRight className="h-3 w-3" />
                              </div>
                            </Link>
                          </NavigationMenuLink>

                          {/* 2. Assessment & Testing */}
                          <NavigationMenuLink asChild>
                            <Link to="/features?tab=assessment" className="group block p-4 rounded-xl border border-border/50 bg-card/50 hover:bg-muted/50 hover:border-purple-500/30 hover:shadow-lg transition-all duration-200">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="p-2 rounded-lg bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors">
                                  <BookOpen className="h-4 w-4 text-purple-500" />
                                </div>
                                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">Integrated</span>
                              </div>
                              <h4 className="font-semibold text-sm text-foreground mb-2">Assessment & Testing</h4>
                              <div className="px-2 py-1 rounded-md bg-purple-500/5 text-xs font-medium text-purple-600 mb-3">⚡ 85% Better Quality</div>
                              <ul className="space-y-2 mb-4">
                                <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Award className="h-3 w-3 text-purple-500 shrink-0" /> Custom Skills Tests
                                </li>
                                <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Play className="h-3 w-3 text-purple-500 shrink-0" /> Video Interviews
                                </li>
                                <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Brain className="h-3 w-3 text-purple-500 shrink-0" /> AI Scoring Engine
                                </li>
                              </ul>
                              <div className="text-xs font-medium text-primary flex items-center gap-1 group-hover:gap-2 transition-all">
                                Explore Assessments <ChevronRight className="h-3 w-3" />
                              </div>
                            </Link>
                          </NavigationMenuLink>

                          {/* 3. HR Management & ESS */}
                          <NavigationMenuLink asChild>
                            <Link to="/features?tab=hrms" className="group block p-4 rounded-xl border border-border/50 bg-card/50 hover:bg-muted/50 hover:border-green-500/30 hover:shadow-lg transition-all duration-200">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="p-2 rounded-lg bg-green-500/10 group-hover:bg-green-500/20 transition-colors">
                                  <Shield className="h-4 w-4 text-green-500" />
                                </div>
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">Featured</span>
                              </div>
                              <h4 className="font-semibold text-sm text-foreground mb-2">HR Management & ESS</h4>
                              <div className="px-2 py-1 rounded-md bg-green-500/5 text-xs font-medium text-green-600 mb-3">⚡ Save 40h/month</div>
                              <ul className="space-y-2 mb-4">
                                <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Users className="h-3 w-3 text-green-500 shrink-0" /> Employee Records
                                </li>
                                <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Calendar className="h-3 w-3 text-green-500 shrink-0" /> Leave Management
                                </li>
                                <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <BarChart3 className="h-3 w-3 text-green-500 shrink-0" /> Performance Reviews
                                </li>
                              </ul>
                              <div className="text-xs font-medium text-primary flex items-center gap-1 group-hover:gap-2 transition-all">
                                Explore HRMS <ChevronRight className="h-3 w-3" />
                              </div>
                            </Link>
                          </NavigationMenuLink>

                          {/* 4. Complete Bundle */}
                          <NavigationMenuLink asChild>
                            <Link to="/pricing?bundle=true" className="group block p-4 rounded-xl border border-border/50 bg-card/50 hover:bg-muted/50 hover:shadow-lg hover:border-orange-500/30 transition-all duration-200">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="p-2 rounded-lg bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors">
                                  <Zap className="h-4 w-4 text-orange-500" />
                                </div>
                                <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">Complete</span>
                              </div>
                              <h4 className="font-semibold text-sm text-foreground mb-2">Complete Bundle</h4>
                              <div className="px-2 py-1 rounded-md bg-orange-500/5 text-xs font-medium text-orange-600 mb-3">⚡ Save 30%</div>
                              <ul className="space-y-2 mb-4">
                                <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Zap className="h-3 w-3 text-orange-500 shrink-0" /> All Features Included
                                </li>
                                <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <HeartHandshake className="h-3 w-3 text-orange-500 shrink-0" /> Priority Support
                                </li>
                                <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <CreditCard className="h-3 w-3 text-orange-500 shrink-0" /> 30% Bundle Discount
                                </li>
                              </ul>
                              <div className="text-xs font-medium text-primary flex items-center gap-1 group-hover:gap-2 transition-all">
                                Build your bundle <ChevronRight className="h-3 w-3" />
                              </div>
                            </Link>
                          </NavigationMenuLink>
                        </div>

                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="text-sm font-medium">Solutions</NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-[900px] p-0 bg-gradient-to-br from-background via-background to-muted/20">
                        {/* Header */}
                        <div className="p-5 border-b bg-gradient-to-r from-emerald-500/5 to-blue-500/5">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-bold text-foreground">Solutions Built for You</h3>
                              <p className="text-xs text-muted-foreground">Tailored HR solutions for every organization size and industry</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <Button asChild size="sm" variant="default" className="text-xs shadow-sm">
                                <Link to="/solutions">View All Solutions →</Link>
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* 3-Column Grid */}
                        <div className="grid grid-cols-3 gap-4 px-6 pb-5 pt-4">
                          {/* Column 1: By Company Size */}
                          <div className="space-y-3">
                            <div className="flex items-center gap-1.5 px-1 pb-1">
                              <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
                              <span className="text-xs font-semibold text-foreground">By Company Size</span>
                            </div>
                            <NavigationMenuLink asChild>
                              <Link to="/business-type-recruitment-software-for-startups" className="group block p-3 rounded-xl border transition-all duration-200 border-border/50 bg-card/50 hover:bg-muted/50 hover:border-blue-500/30 hover:shadow-md">
                                <div className="flex items-start gap-2.5">
                                  <div className="p-1.5 rounded-lg bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                                    <Rocket className="h-4 w-4 text-blue-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 mb-0.5">
                                      <span className="font-semibold text-sm text-foreground">For Startups</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-1.5">Scale fast without HR complexity. Perfect for teams of 1-50.</p>
                                    <div className="text-xs text-primary font-medium">✓ Setup in 10 minutes</div>
                                  </div>
                                </div>
                              </Link>
                            </NavigationMenuLink>

                            <NavigationMenuLink asChild>
                              <Link to="/solutions/medium-business" className="group block p-3 rounded-xl border transition-all duration-200 border-border/50 bg-card/50 hover:bg-muted/50 hover:border-purple-500/30 hover:shadow-md">
                                <div className="flex items-start gap-2.5">
                                  <div className="p-1.5 rounded-lg bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors">
                                    <Building2 className="h-4 w-4 text-purple-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 mb-0.5">
                                      <span className="font-semibold text-sm text-foreground">Medium Business</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-1.5">Advanced features for 100-500 employee companies.</p>
                                    <div className="text-xs text-primary font-medium">✓ Enterprise features</div>
                                  </div>
                                </div>
                              </Link>
                            </NavigationMenuLink>

                            <NavigationMenuLink asChild>
                              <Link to="/business-type-staffing-agency-recruiting-software" className="group block p-3 rounded-xl border transition-all duration-200 border-border/50 bg-card/50 hover:bg-muted/50 hover:border-orange-500/30 hover:shadow-md">
                                <div className="flex items-start gap-2.5">
                                  <div className="p-1.5 rounded-lg bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors">
                                    <Briefcase className="h-4 w-4 text-orange-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 mb-0.5">
                                      <span className="font-semibold text-sm text-foreground">Staffing Agency</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-1.5">Multi-client management and AI-driven placements.</p>
                                    <div className="text-xs text-primary font-medium">✓ Faster placements</div>
                                  </div>
                                </div>
                              </Link>
                            </NavigationMenuLink>
                          </div>

                          {/* Column 2: By Work Style */}
                          <div className="space-y-3">
                            <div className="flex items-center gap-1.5 px-1 pb-1">
                              <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                              <span className="text-xs font-semibold text-foreground">By Work Style</span>
                            </div>
                            <NavigationMenuLink asChild>
                              <Link to="/solutions/remote-teams" className="group block p-3 rounded-xl border transition-all duration-200 border-border/50 bg-card/50 hover:bg-muted/50 hover:border-violet-500/30 hover:shadow-md">
                                <div className="flex items-start gap-2.5">
                                  <div className="p-1.5 rounded-lg bg-violet-500/10 group-hover:bg-violet-500/20 transition-colors">
                                    <Globe className="h-4 w-4 text-violet-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 mb-0.5">
                                      <span className="font-semibold text-sm text-foreground">Remote Teams</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-1.5">Distributed workforce management made simple.</p>
                                    <div className="text-xs text-primary font-medium">✓ Global compliance ready</div>
                                  </div>
                                </div>
                              </Link>
                            </NavigationMenuLink>

                            <NavigationMenuLink asChild>
                              <Link to="/solutions/employee-experience" className="group block p-3 rounded-xl border transition-all duration-200 border-border/50 bg-card/50 hover:bg-muted/50 hover:border-pink-500/30 hover:shadow-md">
                                <div className="flex items-start gap-2.5">
                                  <div className="p-1.5 rounded-lg bg-pink-500/10 group-hover:bg-pink-500/20 transition-colors">
                                    <Heart className="h-4 w-4 text-pink-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 mb-0.5">
                                      <span className="font-semibold text-sm text-foreground">Employee Experience</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-1.5">Boost engagement with culture-first HR.</p>
                                    <div className="text-xs text-primary font-medium">✓ 90% satisfaction rate</div>
                                  </div>
                                </div>
                              </Link>
                            </NavigationMenuLink>

                            <NavigationMenuLink asChild>
                              <Link to="/solutions/hybrid-work" className="group block p-3 rounded-xl border transition-all duration-200 border-border/50 bg-card/50 hover:bg-muted/50 hover:border-indigo-500/30 hover:shadow-md">
                                <div className="flex items-start gap-2.5">
                                  <div className="p-1.5 rounded-lg bg-indigo-500/10 group-hover:bg-indigo-500/20 transition-colors">
                                    <Laptop className="h-4 w-4 text-indigo-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 mb-0.5">
                                      <span className="font-semibold text-sm text-foreground">Hybrid Work</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-1.5">One platform for office & remote teams.</p>
                                    <div className="text-xs text-primary font-medium">✓ 55% less scheduling overhead</div>
                                  </div>
                                </div>
                              </Link>
                            </NavigationMenuLink>

                          </div>

                          {/* Column 3: By Industry */}
                          <div className="space-y-3">
                            <div className="flex items-center gap-1.5 px-1 pb-1">
                              <div className="h-2 w-2 bg-orange-500 rounded-full"></div>
                              <span className="text-xs font-semibold text-foreground">By Industry</span>
                            </div>
                            <NavigationMenuLink asChild>
                              <Link to="/solutions/healthcare" className="group block p-3 rounded-xl border transition-all duration-200 border-border/50 bg-card/50 hover:bg-muted/50 hover:border-emerald-500/30 hover:shadow-md">
                                <div className="flex items-start gap-2.5">
                                  <div className="p-1.5 rounded-lg bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
                                    <Hospital className="h-4 w-4 text-emerald-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className="font-semibold text-sm text-foreground">Healthcare</span>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-1.5 mt-0.5">HIPAA-compliant HR for medical organizations.</p>
                                    <div className="text-xs text-primary font-medium">✓ 500+ hospitals trust us</div>
                                  </div>
                                </div>
                              </Link>
                            </NavigationMenuLink>

                            <NavigationMenuLink asChild>
                              <Link to="/solutions/technology" className="group block p-3 rounded-xl border transition-all duration-200 border-border/50 bg-card/50 hover:bg-muted/50 hover:border-blue-500/30 hover:shadow-md">
                                <div className="flex items-start gap-2.5">
                                  <div className="p-1.5 rounded-lg bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                                    <Cpu className="h-4 w-4 text-blue-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className="font-semibold text-sm text-foreground">Technology</span>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-1.5 mt-0.5">Hire engineers & manage tech teams globally.</p>
                                    <div className="text-xs text-primary font-medium">✓ API integrations</div>
                                  </div>
                                </div>
                              </Link>
                            </NavigationMenuLink>

                            <NavigationMenuLink asChild>
                              <Link to="/solutions/manufacturing" className="group block p-3 rounded-xl border transition-all duration-200 border-border/50 bg-card/50 hover:bg-muted/50 hover:border-orange-500/30 hover:shadow-md">
                                <div className="flex items-start gap-2.5">
                                  <div className="p-1.5 rounded-lg bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors">
                                    <Factory className="h-4 w-4 text-orange-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className="font-semibold text-sm text-foreground">Manufacturing</span>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-1.5 mt-0.5">Workforce & safety management for factories.</p>
                                    <div className="text-xs text-primary font-medium">✓ 40% safety improvement</div>
                                  </div>
                                </div>
                              </Link>
                            </NavigationMenuLink>

                          </div>
                        </div>

                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                      <Link to="/pricing" className={`text-sm font-medium transition-colors px-4 py-2 rounded-lg ${
                        isActive('/pricing') 
                          ? 'text-primary font-semibold' 
                          : 'text-nav-link hover:text-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}>
                        Pricing
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                      <Link to="/resources" className={`text-sm font-medium transition-colors px-4 py-2 rounded-lg ${
                        isActive('/resources') 
                          ? 'text-primary font-semibold' 
                          : 'text-nav-link hover:text-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}>
                        Resources
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="text-sm font-medium">
                      Tools
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-[700px] p-0 bg-gradient-to-br from-background via-background to-muted/20">
                        {/* Header Section */}
                        <div className="p-5 border-b bg-gradient-to-r from-green-500/5 to-blue-500/5">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-bold text-foreground">Free HR Tools</h3>
                              <p className="text-xs text-muted-foreground">Powerful calculators and assessments to optimize your HR strategy</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <Button asChild size="sm" variant="default" className="text-xs shadow-sm">
                                <Link to="/tools" onClick={() => trackCtaClick('/tools', 'View All Tools', undefined, { section: 'header', location: 'tools_menu' })}>View All Tools →</Link>
                              </Button>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6 p-6">
                          {/* HR Calculators Column */}
                          <div className="space-y-4">

                            <NavigationMenuLink asChild>
                              <Link
                                to="/tools/roi-calculator"
                                onClick={() => trackCtaClick('/tools/roi-calculator', 'ROI Calculator', undefined, { section: 'header', location: 'tools_menu' })}
                                className={`group block p-4 rounded-xl border transition-all duration-200 ${
                                  isActive('/tools/roi-calculator') 
                                    ? 'border-border bg-muted/50 shadow-sm' 
                                    : 'border-border/50 bg-card/50 hover:bg-muted/50 hover:border-primary/20 hover:shadow-lg'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="p-2 rounded-lg bg-green-500/10 group-hover:bg-green-500/20 transition-colors">
                                    <Calculator className="h-4 w-4 text-green-500" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className="font-semibold text-sm">ROI Calculator</div>
                                    </div>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                                      Calculate your HR technology return on investment
                                    </p>
                                    <div className="text-xs text-primary font-medium">✓ Get results in 3 minutes</div>
                                  </div>
                                  <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </Link>
                            </NavigationMenuLink>

                            <NavigationMenuLink asChild>
                              <Link
                                to="/tools/salary-calculator"
                                onClick={() => trackCtaClick('/tools/salary-calculator', 'Salary Calculator', undefined, { section: 'header', location: 'tools_menu' })}
                                className={`group block p-4 rounded-xl border transition-all duration-200 ${
                                  isActive('/tools/salary-calculator') 
                                    ? 'border-border bg-muted/50 shadow-sm' 
                                    : 'border-border/50 bg-card/50 hover:bg-muted/50 hover:border-primary/20 hover:shadow-lg'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="p-2 rounded-lg bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors">
                                    <CreditCard className="h-4 w-4 text-purple-500" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className="font-semibold text-sm">Salary Calculator</div>
                                    </div>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                                      Calculate competitive salary ranges by role and location
                                    </p>
                                    <div className="text-xs text-primary font-medium">✓ Market data from 50M+ salaries</div>
                                  </div>
                                  <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </Link>
                            </NavigationMenuLink>

                            <NavigationMenuLink asChild>
                              <Link
                                to="/tools/cost-calculator"
                                onClick={() => trackCtaClick('/tools/cost-calculator', 'Cost Calculator', undefined, { section: 'header', location: 'tools_menu' })}
                                className={`group block p-4 rounded-xl border transition-all duration-200 ${
                                  isActive('/tools/cost-calculator') 
                                    ? 'border-border bg-muted/50 shadow-sm' 
                                    : 'border-border/50 bg-card/50 hover:bg-muted/50 hover:border-primary/20 hover:shadow-lg'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="p-2 rounded-lg bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors">
                                    <BarChart3 className="h-4 w-4 text-orange-500" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-semibold text-sm mb-1">Cost Calculator</div>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                                      Calculate true employee costs including benefits
                                    </p>
                                    <div className="text-xs text-primary font-medium">✓ Includes hidden costs</div>
                                  </div>
                                  <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </Link>
                            </NavigationMenuLink>
                          </div>

                          {/* Assessment Tools Column */}
                          <div className="space-y-4">

                            <NavigationMenuLink asChild>
                              <Link
                                to="/tools/resume-analyzer"
                                onClick={() => trackCtaClick('/tools/resume-analyzer', 'Resume Analyzer', undefined, { section: 'header', location: 'tools_menu' })}
                                className={`group block p-4 rounded-xl border transition-all duration-200 ${
                                  isActive('/tools/resume-analyzer') 
                                    ? 'border-border bg-muted/50 shadow-sm' 
                                    : 'border-border/50 bg-card/50 hover:bg-muted/50 hover:border-primary/20 hover:shadow-lg'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="p-2 rounded-lg bg-violet-500/10 group-hover:bg-violet-500/20 transition-colors">
                                    <Brain className="h-4 w-4 text-violet-500" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className="font-semibold text-sm">Resume Analyzer</div>
                                    </div>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                                      AI-powered resume analysis with skill matching
                                    </p>
                                    <div className="text-xs text-primary font-medium">✓ 95% accuracy rate</div>
                                  </div>
                                  <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </Link>
                            </NavigationMenuLink>

                            <NavigationMenuLink asChild>
                              <Link
                                to="/tools/skills-assessment"
                                onClick={() => trackCtaClick('/tools/skills-assessment', 'Skills Assessment', undefined, { section: 'header', location: 'tools_menu' })}
                                className={`group block p-4 rounded-xl border transition-all duration-200 ${
                                  isActive('/tools/skills-assessment') 
                                    ? 'border-border bg-muted/50 shadow-sm' 
                                    : 'border-border/50 bg-card/50 hover:bg-muted/50 hover:border-primary/20 hover:shadow-lg'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="p-2 rounded-lg bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                                    <Target className="h-4 w-4 text-blue-500" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-semibold text-sm mb-1">Skills Assessment</div>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                                      Evaluate candidate and employee skills
                                    </p>
                                    <div className="text-xs text-primary font-medium">✓ 100+ skill categories</div>
                                  </div>
                                  <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </Link>
                            </NavigationMenuLink>

                            <NavigationMenuLink asChild>
                              <Link
                                to="/tools/hr-audit"
                                onClick={() => trackCtaClick('/tools/hr-audit', 'HR Audit Tool', undefined, { section: 'header', location: 'tools_menu' })}
                                className={`group block p-4 rounded-xl border transition-all duration-200 ${
                                  isActive('/tools/hr-audit') 
                                    ? 'border-border bg-muted/50 shadow-sm' 
                                    : 'border-border/50 bg-card/50 hover:bg-muted/50 hover:border-primary/20 hover:shadow-lg'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="p-2 rounded-lg bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
                                    <Shield className="h-4 w-4 text-emerald-500" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className="font-semibold text-sm">HR Audit Tool</div>
                                    </div>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                                      Complete HR system assessment and recommendations
                                    </p>
                                    <div className="text-xs text-primary font-medium">✓ 15-page detailed report</div>
                                  </div>
                                  <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </Link>
                            </NavigationMenuLink>
                          </div>
                        </div>

                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                      <Link to="/blog" className={`text-sm font-medium transition-colors px-4 py-2 rounded-lg ${
                        isActive('/blog') 
                          ? 'text-primary font-semibold' 
                          : 'text-nav-link hover:text-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}>
                        Blog
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="text-sm font-medium">
                      Company
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-[700px] p-0 bg-gradient-to-br from-background via-background to-muted/20">
                        {/* Header Section */}
                        <div className="p-5 border-b bg-gradient-to-r from-blue-500/5 to-purple-500/5">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-bold text-foreground">About Hirebee</h3>
                              <p className="text-xs text-muted-foreground">Building the future of HR management since 2020</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <span className="text-sm font-bold text-foreground">5+</span>
                                <p className="text-[10px] text-muted-foreground">Years Strong</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4 p-6">
                            <NavigationMenuLink asChild>
                              <Link
                                to="/our-story"
                                className={`group block p-4 rounded-xl border transition-all duration-200 ${
                                  isActive('/our-story') 
                                    ? 'border-border bg-muted/50 shadow-sm' 
                                    : 'border-border/50 bg-card/50 hover:bg-muted/50 hover:border-primary/20 hover:shadow-lg'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="p-2 rounded-lg bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                                    <Building2 className="h-4 w-4 text-blue-500" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-semibold text-sm mb-1">About Us</div>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                                      Our mission to revolutionize HR management
                                    </p>
                                    <div className="text-xs text-primary font-medium">✓ Founded by HR experts</div>
                                  </div>
                                  <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </Link>
                            </NavigationMenuLink>

                            <NavigationMenuLink asChild>
                              <Link
                                to="/team"
                                className={`group block p-4 rounded-xl border transition-all duration-200 ${
                                  isActive('/team')
                                    ? 'border-border bg-muted/50 shadow-sm' 
                                    : 'border-border/50 bg-card/50 hover:bg-muted/50 hover:border-primary/20 hover:shadow-lg'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="p-2 rounded-lg bg-green-500/10 group-hover:bg-green-500/20 transition-colors">
                                    <Users className="h-4 w-4 text-green-500" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-semibold text-sm mb-1">Our Team</div>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                                      Meet the passionate people behind Hirebee
                                    </p>
                                    <div className="text-xs text-primary font-medium">✓ 50+ team members</div>
                                  </div>
                                  <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </Link>
                            </NavigationMenuLink>

                            <NavigationMenuLink asChild>
                              <Link
                                to="/press"
                                className={`group block p-4 rounded-xl border transition-all duration-200 ${
                                  isActive('/press')
                                    ? 'border-border bg-muted/50 shadow-sm' 
                                    : 'border-border/50 bg-card/50 hover:bg-muted/50 hover:border-primary/20 hover:shadow-lg'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="p-2 rounded-lg bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors">
                                    <MessageCircle className="h-4 w-4 text-orange-500" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className="font-semibold text-sm">Press & Media</div>
                                    </div>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                                      News, announcements, and media kit
                                    </p>
                                    <div className="text-xs text-primary font-medium">✓ Featured in 200+ publications</div>
                                  </div>
                                  <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </Link>
                            </NavigationMenuLink>
                        </div>

                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            </div>
            
            {/* Desktop CTA & Auth Buttons */}
            <div className="hidden md:flex items-center space-x-2 lg:space-x-3 flex-shrink-0">
              <Button
                size="default"
                variant="outline"
                className="font-semibold tracking-wide"
                onClick={() => {
                  trackCtaClick('login_modal', 'Log In', undefined, { section: 'header', location: 'above_fold' });
                  setLoginModalOpen(true);
                }}
              >
                Log In
              </Button>
              <Button
                size="default"
                variant="primary"
                className="shadow-medium"
                onClick={() => {
                  trackCtaClick('/pricing', 'Get Started', undefined, { section: 'header', location: 'above_fold' });
                  navigate('/pricing');
                }}
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Get Started - Right */}
            <div className="md:hidden flex items-center">
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  trackCtaClick('/pricing', 'Get Started', undefined, { section: 'header', location: 'above_fold' });
                  navigate('/pricing');
                }}
              >
                Get Started
              </Button>
              {/* PERF: mounted only once the burger has been tapped -- see MobileMenu.tsx. */}
              {mobileMenuMounted && (
                <MobileMenu
                  open={mobileMenuOpen}
                  onOpenChange={setMobileMenuOpen}
                  onLoginClick={() => setLoginModalOpen(true)}
                />
              )}
            </div>
          </div>
        </div>
      </nav>
      </div>{/* end fixed header wrapper */}
      <ProductSelectionModal open={productModalOpen} onOpenChange={setProductModalOpen} />
      <ProductSelectionModal open={loginModalOpen} onOpenChange={setLoginModalOpen} mode="login" />
    </>
  );
}
